import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import '../../styles/EventsStyles/RegistrationForm.css';
import PrivacyModal from "./PrivacyModal";
import { REGISTRATION_CONTACT, ROLES } from "../../webinars/config";
import { RegistrationReceipt, Webinar, isRegistrationError } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { saveReceipt } from "../../webinars/receipts";
import { RegistrationErrors, validateRegistration } from "../../webinars/shared/validation";
import { useCooldown } from "../../hooks/useCooldown";

interface RegistrationFormProps {
  webinar: Webinar;
}

const emptyForm = {
  firstName: "",
  surname: "",
  email: "",
  phone: "",
  organisation: "",
  role: "",
  roleOther: "",
  privacy: false,
  marketing: false,
  website: "", // honeypot: hidden from people, bots tend to fill it in
};

type FormState = typeof emptyForm;
type ErrorKey = "firstName" | "surname" | "email" | "phone" | "organisation" | "privacy";

/** The shared validator (the server runs the same one) names fields after the database columns */
const toFormErrors = (errors: RegistrationErrors): Partial<Record<ErrorKey, string>> => {
  const mapped: Partial<Record<ErrorKey, string | undefined>> = {
    firstName: errors.firstName,
    surname: errors.lastName,
    email: errors.email,
    phone: errors.phone,
    organisation: errors.organisation ?? errors.professionalRole,
    privacy: errors.privacyNoticeAccepted,
  };
  return Object.fromEntries(Object.entries(mapped).filter(([, message]) => message)) as Partial<Record<ErrorKey, string>>;
};

export default function RegistrationForm({ webinar }: RegistrationFormProps) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<ErrorKey, string>>>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  // After too many attempts the server says how long to wait; the button stays off until then
  const cooldown = useCooldown();
  // How long the form was open: the server treats near-instant submissions as bots
  const openedAt = useRef(Date.now());

  // Update one field and clear its error
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || cooldown.active) return;

    const role = form.role === "Other" ? (form.roleOther.trim() ? `Other: ${form.roleOther.trim()}` : "Other") : form.role;
    const checked = validateRegistration({
      webinarId: webinar.id,
      firstName: form.firstName,
      lastName: form.surname,
      email: form.email,
      phone: form.phone,
      organisation: form.organisation,
      professionalRole: role,
      privacyNoticeAccepted: form.privacy,
      marketingConsent: form.marketing,
    });
    if (!checked.ok) {
      setErrors(toFormErrors(checked.errors));
      setFormError("Please check the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await webinarRepository.register(checked.value, {
        website: form.website,
        elapsedMs: Date.now() - openedAt.current,
      });
      const receipt: RegistrationReceipt = {
        ...result,
        firstName: checked.value.firstName,
        lastName: checked.value.lastName,
        email: checked.value.email,
        phone: checked.value.phone,
        webinar: {
          id: webinar.id,
          slug: webinar.slug,
          title: webinar.title,
          startAt: webinar.startAt,
          endAt: webinar.endAt,
          meetingPlatform: webinar.meetingPlatform,
        },
      };
      saveReceipt(receipt);
      navigate(`/events/${webinar.slug}/registered?ref=${encodeURIComponent(result.reference)}`, { state: { receipt } });
    } catch (error) {
      if (isRegistrationError(error) && error.code === "duplicate") {
        setErrors({ email: "Already registered for this webinar." });
        setFormError("This email address is already registered for this webinar. You don't need to register again: your joining instructions will be emailed to you before the session.");
      } else if (isRegistrationError(error) && error.code === "rate_limited") {
        cooldown.start(Date.now() + (error.retryAfter ?? 600) * 1000);
        setFormError(error.message);
      } else if (isRegistrationError(error) && error.code === "invalid" && error.fieldErrors) {
        setErrors(toFormErrors(error.fieldErrors));
        setFormError(error.message);
      } else if (isRegistrationError(error) && error.code !== "unavailable") {
        setFormError(error.message);
      } else {
        console.error(error);
        setFormError(`We couldn't save your registration. Please try again, or contact us on WhatsApp at ${REGISTRATION_CONTACT.phone}.`);
      }
      setSubmitting(false);
    }
  };

  // Shared props for each text field: error styling + screen-reader link to the message
  const fieldProps = (key: ErrorKey) => ({
    id: `reg-${key}`,
    "aria-invalid": errors[key] ? true : undefined,
    "aria-describedby": errors[key] ? `reg-${key}-error` : undefined,
    className: errors[key] ? "has-error" : undefined,
  });

  const fieldError = (key: ErrorKey) =>
    errors[key] ? <small className="ev-field-error" id={`reg-${key}-error`}>{errors[key]}</small> : null;

  return (
    <>
      <form className="ev-form" onSubmit={handleSubmit} noValidate>
        <div className="ev-form-row">
          <label className="ev-field" htmlFor="reg-firstName">
            First name *
            <input type="text" autoComplete="given-name" value={form.firstName} onChange={e => set("firstName", e.target.value)} {...fieldProps("firstName")} />
            {fieldError("firstName")}
          </label>
          <label className="ev-field" htmlFor="reg-surname">
            Surname *
            <input type="text" autoComplete="family-name" value={form.surname} onChange={e => set("surname", e.target.value)} {...fieldProps("surname")} />
            {fieldError("surname")}
          </label>
        </div>

        <label className="ev-field" htmlFor="reg-email">
          Email address *
          <input type="email" autoComplete="email" value={form.email} onChange={e => set("email", e.target.value)} {...fieldProps("email")} />
          {fieldError("email")}
        </label>

        <label className="ev-field" htmlFor="reg-phone">
          Phone number *
          <input type="tel" autoComplete="tel" placeholder="e.g. 082 123 4567" value={form.phone} onChange={e => set("phone", e.target.value)} {...fieldProps("phone")} />
          {fieldError("phone")}
        </label>

        <label className="ev-field" htmlFor="reg-organisation">
          Company or organisation <span className="ev-optional">(optional)</span>
          <input type="text" autoComplete="organization" value={form.organisation} onChange={e => set("organisation", e.target.value)} {...fieldProps("organisation")} />
          {fieldError("organisation")}
        </label>

        {/* Honeypot — invisible to people, so only bots fill it in */}
        <div className="ev-honeypot" aria-hidden="true">
          <label htmlFor="reg-website">Website</label>
          <input id="reg-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => set("website", e.target.value)} />
        </div>

        <fieldset className="ev-roles-field">
          <legend>Professional role <span className="ev-optional">(optional)</span></legend>
          {ROLES.map(role => (
            <label key={role} className={`ev-role ${form.role === role ? "is-selected" : ""}`}>
              <input type="radio" name="role" value={role} checked={form.role === role} onChange={() => set("role", role)} />
              {role}
            </label>
          ))}
          {form.role === "Other" && (
            <input
              type="text"
              className="ev-role-other"
              aria-label="Your role"
              placeholder="Tell us your role"
              value={form.roleOther}
              onChange={e => set("roleOther", e.target.value)}
            />
          )}
        </fieldset>

        <div className="ev-consents">
          <label className="ev-check">
            <input type="checkbox" checked={form.privacy} onChange={e => set("privacy", e.target.checked)} aria-describedby={errors.privacy ? "reg-privacy-error" : undefined} />
            <span>
              I have read the{" "}
              <button type="button" className="ev-inline-link" onClick={() => setPrivacyOpen(true)}>privacy notice</button>{" "}
              and understand how my registration information will be used to administer this webinar. *
            </span>
          </label>
          {errors.privacy && <small className="ev-field-error" id="reg-privacy-error">{errors.privacy}</small>}
          <label className="ev-check ev-check--muted">
            <input type="checkbox" checked={form.marketing} onChange={e => set("marketing", e.target.checked)} />
            <span>Optional: email me about future Kole Medical webinars and events.</span>
          </label>
        </div>

        {formError && <div className="ev-form-error" role="alert">{formError}</div>}

        <button type="submit" className="km-btn km-btn--primary km-btn--block" disabled={submitting || cooldown.active}>
          {submitting ? <><span className="km-spinner" aria-hidden="true" />Registering…</>
            : cooldown.active ? `Try again in ${cooldown.label}`
            : "Register"}
        </button>
      </form>

      {privacyOpen && <PrivacyModal onClose={() => setPrivacyOpen(false)} />}
    </>
  );
}
